<template>
  <div
    class="profile-menu"
    @click="toggle($event)"
    :class="{ 'active': menuOpen }"
  >
    <Skeleton width="230px" height="50px" v-if="loading" />
    <div class="profile-trigger" v-if="!loading">
      <div class="avatar-container">
        <Avatar
          v-if="!profileData.profile_picture_path && !loading"
          :label="getInitials"
          class="avatar"
          size="normal"
          shape="circle"
        />
        <Avatar
          v-else
          :image="logoURL + profileData.profile_picture_path"
          class="avatar"
          size="normal"
          shape="circle"
        />
      </div>
      
      <div class="profile-info">
        <span v-if="profileData" class="profile-name">
          {{ profileData.firstname }} {{ profileData.lastname }}
        </span>
        <div class="role-points-container">
          <span class="profile-role">
            {{ profileData.role_name }}
          </span>
          <div v-if="shouldShowPoints" class="points-badge">
            <i :class="`pi pi-${profileData.points > 0 ? 'star-fill' : 'star'}`" class="points-icon"></i>
            <span class="points-value">{{ profileData.points }} Points</span>
          </div>
        </div>
      </div>
      
      <i class="pi pi-chevron-down dropdown-icon"></i>
    </div>
  </div>

  <Menu ref="menu" id="profile_menu" :model="items" :popup="true" class="profile-dropdown">
    <template #item="{ item }">
      <button
        v-if="!item.route"
        type="button"
        class="menu-item"
        :class="{ 'danger-item': item.label === 'Logout' }"
        @click="item.command"
      >
        <span class="menu-item-icon">
          <i :class="item.icon"></i>
        </span>
        <span class="menu-item-label">{{ item.label }}</span>
      </button>
      <router-link
        v-else
        :to="{ name: item.route }"
        class="menu-item"
      >
        <span class="menu-item-icon">
          <i :class="item.icon"></i>
        </span>
        <span class="menu-item-label">{{ item.label }}</span>
      </router-link>
    </template>
  </Menu>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { get, post, logoURL } from '@/api/Services'

export default {
  name: 'AppInlineMenu',
  data() {
    return {
      logoURL: logoURL,
      menuOpen: false,
      loading: true,
      items: [
        { label: 'User Profile', icon: 'pi pi-user', route: 'profile' },
        {
          label: 'Change Password',
          icon: 'pi pi-lock',
          route: 'changePassword',
        },
        {
          label: 'Help',
          icon: 'pi pi-question',
          command: () => {
            this.$router.push({ name: 'faq' })
          },
        },
        {
          label: 'Logout',
          icon: 'pi pi-sign-out',
          command: this.logoutUser,
        },
      ],
    }
  },
  created() {
    // Sayfa yüklendiğinde loading'i false yap
    this.loading = false
  },
  mounted() {
    // Use nextTick to ensure component is fully rendered before adding event listener
    this.$nextTick(() => {
      // Close menu when clicking outside
      document.addEventListener('click', this.handleOutsideClick);
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    toggle(event) {
      event.stopPropagation();
      this.menuOpen = !this.menuOpen
      this.$refs.menu.toggle(event)
    },
    handleOutsideClick(event) {
      // Skip processing if menu isn't open or refs aren't available
      if (!this.menuOpen || !this.$refs.menu) return;
      
      try {
        // Find the menu element using the ref
        const menu = this.$refs.menu.$el;
        
        // Check if the click was outside both the menu and trigger element
        const isClickOutsideMenu = menu && !menu.contains(event.target);
        const isClickOutsideTrigger = !this.$el || !this.$el.contains(event.target);
        
        if (isClickOutsideMenu && isClickOutsideTrigger) {
          this.menuOpen = false;
        }
      } catch (error) {
        // Gracefully handle any errors
        console.error('Error in handleOutsideClick:', error);
        this.menuOpen = false;
      }
    },
    isMobile() {
      return window.innerWidth <= 1024
    },
    async logoutUser() {
      try {
        // First dispatch the logout action and wait for it to complete
        await this.$store.dispatch('account/logout')

        // Then navigate to login page
        this.$router.push({ name: 'login' })
      } catch (error) {
        console.error('Error during logout:', error)
        // Force navigation to login page even if there was an error
        this.$router.push({ name: 'login' })
      }
    },
    ...mapActions('account', ['logout']),
  },
  computed: {
    profileChange() {
      return this.$store.getters.profileRefresh
    },
    ...mapState({
      account: (state) => state.account,
      userParam: (state) => state.account.userParam,
    }),
    getInitials() {
      if (
        !this.profileData ||
        !this.profileData.firstname ||
        !this.profileData.lastname
      ) {
        return ''
      }

      return (
        (this.profileData.firstname[0]?.toUpperCase() || '') +
        (this.profileData.lastname[0]?.toUpperCase() || '')
      )
    },
    shouldShowPoints() {
      return (
        this.userParam &&
        this.userParam.role_id === 2 &&
        this.userParam.points !== undefined
      )
    },
    // Profil bilgilerini userParam'dan al
    profileData() {
      // If userParam is null or undefined, return empty values
      if (!this.userParam) {
        return {
          firstname: '',
          lastname: '',
          role_name: '',
          profile_picture_path: '',
          points: 0,
        }
      }

      return {
        firstname: this.userParam.firstname || '',
        lastname: this.userParam.lastname || '',
        role_name: this.userParam.role_name || '',
        profile_picture_path: this.userParam.profile_picture_path || '',
        points: this.userParam.points || 0,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #015cac;
$accent-color: #fc6c2a;
$text-color: #333;
$light-gray: #f8f9fa;
$medium-gray: #e9ecef;
$dark-gray: #6c757d;
$transition-fast: 0.2s ease;
$transition-normal: 0.3s ease;

// User profile section
.profile-menu {
  cursor: pointer;
  transition: all $transition-fast;
  
  &.active {
    .profile-trigger {
      background-color: rgba($primary-color, 0.08);
    }
  }
}

.profile-trigger {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  gap: 0.75rem;
  transition: background-color $transition-fast;
  
  &:hover {
    background-color: rgba($primary-color, 0.05);
  }
}

.avatar-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar {
  width: 40px;
  height: 40px;
  color: #444;
  font-weight: 600;
  background-color: #dbdde0 !important;
}

::v-deep(.p-avatar img) {
  object-fit: cover;
}

.profile-info {
  display: flex;
  flex-direction: column;
  
  @media (max-width: 1200px) {
    display: none;
  }
}

.profile-name {
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.2;
}

.role-points-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.profile-role {
  color: #fff;
  font-size: 0.8rem;
  line-height: 1.2;
}

.points-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: rgba(255, 215, 0, 0.15);
  border-radius: 10px;
  padding: 2px 6px;
  max-width: fit-content;
  border: 1px solid rgba(255, 215, 0, 0.2);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 215, 0, 0.2);
  }
}

.points-icon {
  color: #ffd700;
  font-size: 0.75rem;
}

.points-value {
  font-weight: 500;
  color: #fff;
  font-size: 0.7rem;
  margin-left: 3px;
}

.dropdown-icon {
  font-size: 0.8rem;
  color: #fff;
  transition: transform $transition-fast;
  
  &.active {
    transform: rotate(180deg);
  }
  
  @media (max-width: 1200px) {
    display: none;
  }
}

// Profile dropdown
::v-deep(.p-menu.profile-dropdown) {
  border: none;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  padding: 0.5rem;
  min-width: 220px;
  
  .p-menuitem {
    margin-bottom: 0.25rem;
  }
  
  .p-menu-list {
    padding: 0;
  }
}

.menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  transition: all $transition-fast;
  
  &:hover {
    background-color: rgba($primary-color, 0.08);
  }
  
  &.danger-item {
    color: #dc3545;
    
    &:hover {
      background-color: rgba(#dc3545, 0.08);
    }
    
    .menu-item-icon i {
      color: #dc3545;
    }
  }
}

.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  
  i {
    font-size: 1rem;
    color: $primary-color;
  }
}

.menu-item-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: $text-color;
}
</style>
